// The command palette's model — pure. No DOM, no queries, no router.
//
// Three groups, one shape (`Command`): the screens in the route table, the
// account's repos, and its recent sessions. Two rules hold it honest:
//
//  - **Every route STATES whether the palette reaches it.** The page group is
//    the route tables themselves: each entry's REQUIRED `label` (lib/routes.ts)
//    is the destination's name, so adding a screen without deciding is a
//    compile error rather than a screen findable only by typing its URL. That
//    matters here more than in the sidebar: the rail deliberately shows a
//    subset, and this is what the *rest* of the app is reachable through.
//  - **`null` is a real answer, and the rule for it is mechanical.** A path
//    carrying a param or a wildcard has no single address to jump to, so it
//    cannot be a destination. `onboarding` was a static exception until the
//    funnel's own one-way doors were the bug being fixed; `invite` is the one
//    that remains, and for a reason the path shape cannot express — it is
//    addressed by a `?token=` from an email, so the palette would be offering a
//    page that can only ever refuse. `command-palette.test.ts` pins both the
//    rule and that single exception, so neither is merely described.

import { t } from '../i18n';
import type { AccountSession } from './overview';
import type { RepoRow } from './repos';
import { ORG_ROUTE_TABLE, type RouteEntry, TOP_ROUTE_TABLE } from './routes';

export type CommandGroup = 'pages' | 'repos' | 'sessions';

export type Command = {
  /** Stable within a render — also the option's DOM id. */
  readonly id: string;
  readonly group: CommandGroup;
  /** Already translated: the primary line, and what the match runs over. */
  readonly label: string;
  /** Secondary line — the path for a screen, the context for a row. Also matched. */
  readonly hint: string;
  /** Router destination, org-resolved by the caller's `resolve`. */
  readonly href: string;
};

/**
 * Display ceiling for the two SERVER-backed groups. Pages get none: that group
 * is a fixed table this bundle already holds whole, so showing all of it is
 * bounded by construction — and it is the group a reader is most often after.
 * Repos and sessions grow, and an account with 200 repos would otherwise bury
 * every screen under them.
 */
export const DYNAMIC_GROUP_CAP = 8;

/** The screens, org-resolved. `adminHost` picks which table is in play (#754). */
export function pageCommands(
  adminHost: boolean,
  resolve: (href: string) => string,
): readonly Command[] {
  const table: readonly RouteEntry[] = adminHost ? TOP_ROUTE_TABLE : ORG_ROUTE_TABLE;
  return table.flatMap((entry) => {
    if (entry.label === null) return [];
    // The admin radar is never org-prefixed — it is cross-account by definition.
    const href = adminHost ? entry.path : resolve(entry.path);
    return [{ id: `page:${entry.key}`, group: 'pages', label: t(entry.label), hint: href, href }];
  });
}

/** The account's repos. Rows come from `useRepoList`, which walks every page. */
export function repoCommands(
  rows: readonly RepoRow[],
  resolve: (href: string) => string,
): readonly Command[] {
  return rows.map((repo) => ({
    id: `repo:${repo.full_name}`,
    group: 'repos',
    label: repo.full_name,
    hint: t(repo.enabled ? 'palette.repo_enabled' : 'palette.repo_disabled'),
    // Same landing tab the repos grid uses; `full_name` is already one path segment.
    href: resolve(`/repos/${encodeURIComponent(repo.full_name)}/overview`),
  }));
}

/**
 * The recent-sessions HEAD, not the archive — `/v1/sessions` serves a bounded
 * top-N and mints no cursor, so this group can only ever be as deep as that
 * window. The heading says "recent" for exactly that reason.
 */
export function sessionCommands(
  rows: readonly AccountSession[],
  resolve: (href: string) => string,
): readonly Command[] {
  return rows.map((session) => ({
    id: `session:${session.id}`,
    group: 'sessions',
    label: session.entity_title,
    hint: session.repo_full_name,
    href: resolve(`/audit/${encodeURIComponent(session.id)}`),
  }));
}

/** One rendered group: what survived the filter, what the cap hid, whether it loaded. */
export type CommandSection = {
  readonly key: CommandGroup;
  readonly rows: readonly Command[];
  /** How many matches the cap held back. Rendered — a silent cut reads as "that was all". */
  readonly hidden: number;
  /** The source refused. Distinct from "no matches": one means look elsewhere. */
  readonly failed: boolean;
};

/** The option's DOM id — also what `aria-activedescendant` points at. */
export function optionDomId(id: string): string {
  return `palette-option-${id}`;
}

/**
 * Filter, rank and cap one group. The cap decision lives HERE rather than at
 * three call sites, so "pages are never capped" is one rule in one place.
 */
export function buildSection(
  key: CommandGroup,
  commands: readonly Command[],
  query: string,
  failed: boolean,
): CommandSection {
  const matched = matchCommands(commands, query);
  const cap = key === 'pages' ? matched.length : DYNAMIC_GROUP_CAP;
  return { key, rows: matched.slice(0, cap), hidden: Math.max(matched.length - cap, 0), failed };
}

/**
 * Rank one candidate against an already-lowercased needle. Lower is better;
 * `null` is no match. A hit at a word boundary outranks one mid-word so typing
 * "keys" reaches "Keys" before "monkeys".
 */
export function scoreText(needle: string, haystack: string): number | null {
  const hay = haystack.toLowerCase();
  if (hay === needle) return 0;
  if (hay.startsWith(needle)) return 1;
  const at = hay.indexOf(needle);
  if (at < 0) return null;
  return /[\s/\-_:.]/.test(hay[at - 1] ?? '') ? 2 : 3;
}

/**
 * Filter + rank one group. An empty query matches everything in the group's own
 * order (the route table's, the server's) — a palette that showed nothing until
 * you typed would hide the list it exists to expose. Ties keep source order, so
 * a background refetch can't reshuffle the row under the reader's cursor.
 */
export function matchCommands(commands: readonly Command[], query: string): readonly Command[] {
  const needle = query.trim().toLowerCase();
  if (needle === '') return commands;
  return commands
    .map((command, index) => {
      const label = scoreText(needle, command.label);
      const hint = scoreText(needle, command.hint);
      // A hint-only hit ranks below every label hit: the hint is context.
      const score = label ?? (hint === null ? null : hint + 4);
      return { command, index, score };
    })
    .filter((hit): hit is { command: Command; index: number; score: number } => hit.score !== null)
    .sort((a, b) => a.score - b.score || a.index - b.index)
    .map((hit) => hit.command);
}
